/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { useServiceContext } from '../root'
import { Select } from './Select'
import { GroupType } from '~/constants/group'

interface SelectOption {
  value: string
  label: string
  id: number
  systemHelperType: string
  isDisabled?: boolean
}

interface SelectUserGroupProps {
  value?: string | string[] | null
  name: string
  onChange: (value: string | string[] | null) => void
  placeholder?: string
  isMulti?: boolean
  onlyUsers?: boolean
  onlyGroups?: boolean
  isDisabled?: boolean
}

function SelectUserGroup(props: SelectUserGroupProps) {
  const [value, setValue] = useState<string | string[]>([])
  const [usersGroups, setUsersGroups] = useState<SelectOption[]>([])

  const context = useServiceContext()

  const updateValue = React.useCallback(
    (selection: SelectOption | SelectOption[] | null) => {
      const newValue = _.isArray(selection) ? selection.map((s) => s.value) : selection?.value || null
      setValue(newValue || [])
      props.onChange(newValue)
    },
    [props.onChange]
  )

  useEffect(() => {
    // Only update if props.value and value are actually different
    const propsValArr = _.isArray(props.value) ? props.value : props.value ? [props.value] : []
    const valueArr = _.isArray(value) ? value : value ? [value] : []
    if (!_.isEqual(propsValArr, valueArr) && usersGroups.length) {
      if (_.isArray(props.value)) {
        const initialValues: SelectOption[] = []
        props.value.forEach((userGroupItem) => {
          const foundUserGroup = usersGroups.find((ug) => ug.value === userGroupItem)
          if (foundUserGroup) {
            initialValues.push(foundUserGroup)
          }
        })
        if (initialValues.length) {
          updateValue(initialValues)
        }
      } else if (!props.value) {
        setValue([])
      } else {
        const foundUserGroup = usersGroups.find((ug) => ug.value === props.value)
        if (foundUserGroup) {
          updateValue(foundUserGroup)
        }
      }
    }
  }, [props.value, usersGroups, value, updateValue])

  useEffect(() => {
    const usersApiService = context.api.legacy.usersApiService
    usersApiService.getAssembledUsersGroups(false).then((data: any) => {
      const sourceData = props.onlyUsers ? data.users : props.onlyGroups ? data.groups : data.merged

      const usersGroupsFiltered = sourceData.map((user: any) => ({
        value: user._uid,
        label: user.chName,
        id: user.id,
        systemHelperType: user.systemHelperType,
        ...(user?.type === GroupType.CLOUDADMIN_ACL ? { isDisabled: true } : {})
      }))

      setUsersGroups(usersGroupsFiltered)
    })
  }, [])

  return (
    <Select
      placeholder={props.placeholder}
      name={props.name}
      value={value}
      options={usersGroups}
      onChange={(selection: any) => updateValue(selection)}
      showNewOptionAtTop={true}
      searchable
      multi={props.isMulti ? true : false}
      id="2a8b6ccd-ae67-4706-a399-dfef4d0ec5db"
      isDisabled={props.isDisabled}
    />
  )
}

export default SelectUserGroup
